/**
 * Astronomy Calculation Engine
 * 
 * Wrapper for astronomy-calculations.js providing accurate planetary positions
 * using VSOP87 theory and JPL Horizons data when available.
 * 
 * This module provides a TypeScript interface to the comprehensive
 * astronomy calculations library.
 */

// Import the astronomy-calculations module
// Note: This is a browser-compatible class that can be instantiated directly
declare global {
  interface Window {
    AstronomyCalculations?: any;
  }
}

/**
 * Planet result from astronomy calculations
 */
export interface PlanetCalculation {
  longitude: number;
  latitude: number;
  distance: number;
  sign: string;
  degree: number;
  retrograde: boolean;
  velocity?: number;
  source?: string;
}

/**
 * Astronomy calculation engine singleton
 */
class AstronomyEngine {
  private calculator: any = null;
  private initialized: boolean = false;
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize the astronomy calculator
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      try {
        console.log('[AstronomyEngine] Initializing...');
        
        // Check if AstronomyCalculations is already loaded
        if (typeof window !== 'undefined' && window.AstronomyCalculations) {
          this.calculator = new window.AstronomyCalculations();
          this.initialized = true;
          console.log('✅ [AstronomyEngine] Loaded from window.AstronomyCalculations');
          return;
        }

        console.log('[AstronomyEngine] Loading /scripts/astronomy-calculations.js...');
        
        // Otherwise, try to dynamically load it
        // The file is in /scripts/astronomy-calculations.js
        await this.loadScript('/scripts/astronomy-calculations.js');
        
        if (window.AstronomyCalculations) {
          this.calculator = new window.AstronomyCalculations();
          this.initialized = true;
          console.log('✅ [AstronomyEngine] Dynamically loaded and initialized');
        } else {
          console.error('❌ [AstronomyEngine] Script loaded but window.AstronomyCalculations not found');
          throw new Error('AstronomyCalculations not found after loading script');
        }
      } catch (error) {
        console.error('❌ [AstronomyEngine] Failed to initialize:', error);
        throw error;
      }
    })();

    return this.initPromise;
  }

  /**
   * Dynamically load a script
   */
  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  /**
   * Calculate Julian Day from date
   */
  calculateJulianDay(date: Date): number {
    if (!this.calculator) {
      throw new Error('AstronomyEngine not initialized. Call initialize() first.');
    }

    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + 1; // JavaScript months are 0-indexed
    const day = date.getUTCDate();
    const hour = date.getUTCHours();
    const minute = date.getUTCMinutes();
    const second = date.getUTCSeconds();

    return this.calculator.calculateJulianDay(year, month, day, hour, minute, second);
  }

  /**
   * Calculate planetary position
   * 
   * @param planet - Planet name (Sun, Moon, Mercury, Venus, Mars, Jupiter, Saturn, Uranus, Neptune, Pluto, North Node, South Node, Lilith)
   * @param julianDay - Julian Day Number
   * @returns Planet calculation result with longitude, sign, degree, retrograde status
   */
  async calculatePlanetPosition(planet: string, julianDay: number): Promise<PlanetCalculation> {
    if (!this.calculator) {
      throw new Error('AstronomyEngine not initialized. Call initialize() first.');
    }

    try {
      const result = await this.calculator.calculatePlanetPosition(planet, julianDay);
      return result;
    } catch (error) {
      console.error(`Error calculating ${planet} position:`, error);
      throw error;
    }
  }

  /**
   * Calculate all planetary positions for a given date
   */
  async calculateAllPlanets(date: Date): Promise<Map<string, PlanetCalculation>> {
    const julianDay = this.calculateJulianDay(date);
    const planets = [
      'Sun', 'Moon', 'Mercury', 'Venus', 'Mars',
      'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto',
      'North Node', 'South Node'
    ];

    const results = new Map<string, PlanetCalculation>();

    // Calculate all planets in parallel for better performance
    const calculations = await Promise.all(
      planets.map(async (planet) => {
        try {
          const position = await this.calculatePlanetPosition(planet, julianDay);
          return { planet, position };
        } catch (error) {
          console.error(`Failed to calculate ${planet}:`, error);
          return null;
        }
      })
    );

    // Store results
    calculations.forEach((result) => {
      if (result) {
        results.set(result.planet, result.position);
      }
    });

    return results;
  }

  /**
   * Calculate Local Sidereal Time
   */
  calculateLST(julianDay: number, longitude: number): number {
    if (!this.calculator) {
      throw new Error('AstronomyEngine not initialized. Call initialize() first.');
    }

    return this.calculator.calculateLST(julianDay, longitude);
  }

  /**
   * Calculate Ascendant (Rising Sign)
   * 
   * @param ramc - Right Ascension of Midheaven in degrees
   * @param latitude - Geographic latitude in degrees (North positive, South negative)
   * @param obliquity - Obliquity of the ecliptic in degrees
   * @returns Ascendant ecliptic longitude in degrees
   */
  calculateAscendant(ramc: number, latitude: number, obliquity: number): number {
    if (!this.calculator) {
      throw new Error('AstronomyEngine not initialized. Call initialize() first.');
    }

    return this.calculator.calculateAscendant(ramc, latitude, obliquity, false);
  }

  /**
   * Calculate house cusps using specified house system
   * 
   * @param ascendant - Ascendant longitude in degrees
   * @param midheaven - Midheaven longitude in degrees
   * @param latitude - Geographic latitude in degrees
   * @param obliquity - Obliquity of the ecliptic in degrees
   * @param ramc - Right Ascension of Midheaven in degrees
   * @param houseSystem - House system: 'placidus', 'equal', 'whole_sign', 'porphyry'
   * @returns House data with cusps array [1-12]
   */
  calculateHouseCusps(
    ascendant: number,
    midheaven: number,
    latitude: number,
    obliquity: number,
    ramc: number,
    houseSystem: 'placidus' | 'equal' | 'whole_sign' | 'porphyry' = 'placidus'
  ): { cusps: number[]; system: string } {
    if (!this.calculator) {
      throw new Error('AstronomyEngine not initialized. Call initialize() first.');
    }

    let housesData;
    switch (houseSystem) {
      case 'placidus':
        // Check for high latitude - use fallback if needed
        if (Math.abs(latitude) > 66.5) {
          console.warn(`Placidus unstable at latitude ${latitude}°. Using Porphyry fallback.`);
          housesData = this.calculator.calculatePorphyryHouses(ascendant, midheaven);
        } else {
          housesData = this.calculator.calculatePlacidusHouses(ascendant, midheaven, latitude, obliquity, ramc);
        }
        break;
      case 'equal':
        housesData = this.calculator.calculateEqualHouses(ascendant);
        break;
      case 'whole_sign':
        housesData = this.calculator.calculateWholeSignHouses(ascendant);
        break;
      case 'porphyry':
        housesData = this.calculator.calculatePorphyryHouses(ascendant, midheaven);
        break;
      default:
        console.warn(`House system '${houseSystem}' not supported. Using Placidus.`);
        housesData = this.calculator.calculatePlacidusHouses(ascendant, midheaven, latitude, obliquity, ramc);
    }

    return housesData;
  }

  /**
   * Calculate tropical reference (obliquity and nutation)
   */
  calculateTropicalReference(julianDay: number): any {
    if (!this.calculator) {
      throw new Error('AstronomyEngine not initialized. Call initialize() first.');
    }

    return this.calculator.calculateTropicalReference(julianDay);
  }

  /**
   * Calculate Midheaven (MC)
   */
  calculateMidheaven(ramc: number, obliquity: number): number {
    if (!this.calculator) {
      throw new Error('AstronomyEngine not initialized. Call initialize() first.');
    }

    return this.calculator.calculateMidheaven(ramc, obliquity);
  }

  /**
   * Check if calculator is ready
   */
  isReady(): boolean {
    return this.initialized && this.calculator !== null;
  }
}

// Export singleton instance
export const astronomyEngine = new AstronomyEngine();

// Export convenience function for direct use
export async function initializeAstronomyEngine(): Promise<void> {
  await astronomyEngine.initialize();
}
